import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { Alert, Button, TextArea } from '../../components/ui'
import { ApiError, ContractError, NetworkError } from '../../api/client'
import { keys } from '../../api/keys'
import { setThreadMode } from '../../api/threads'
import { t } from '../../i18n'
import { useSession } from '../../auth/session'

function messageFor(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof NetworkError) return t('errors.network')
  if (error instanceof ContractError) return t('errors.badResponse')
  return t('pause.failed')
}

/**
 * Pausing the assistant on this one conversation, and letting it go again.
 *
 * Three things here are decisions rather than styling:
 *
 *   - Paused is a state of the conversation, not the absence of a pressed
 *     button. It is a banner in the reading order, above the messages, so that
 *     "why has the assistant said nothing here" is answered before it is asked.
 *   - Pausing asks first; resuming does not. The asymmetry is the point: a
 *     mis-tap that pauses a conversation goes unnoticed for a day, while a
 *     mis-tap that resumes one is visible in the next draft that appears.
 *   - This button is the only thing that pauses anything. The owner answering a
 *     client from their phone does not, and the copy says so where somebody is
 *     most likely to believe otherwise (docs/adr/0003).
 */
export function ThreadModeControl({
  threadId,
  mode,
}: {
  readonly threadId: string
  readonly mode: string
}) {
  const { can } = useSession()
  const queryClient = useQueryClient()

  const [confirming, setConfirming] = useState(false)
  const [reason, setReason] = useState('')
  const [failure, setFailure] = useState<string | null>(null)

  const change = useMutation({
    mutationFn: setThreadMode,
    onSuccess: () => {
      setConfirming(false)
      setReason('')
      setFailure(null)
      // The mode on screen is the one the server stores. Nothing here writes it
      // into the cache by hand, which is what makes a refusal harmless: there is
      // no optimistic copy left claiming the change happened.
      void queryClient.invalidateQueries({ queryKey: keys.thread(threadId) })
      void queryClient.invalidateQueries({ queryKey: keys.anyThreadList() })
    },
    onError: (error) => {
      setConfirming(false)
      setFailure(messageFor(error))
    },
  })

  return (
    <ThreadModeView
      mode={mode}
      mayPause={can('threads.pause')}
      busy={change.isPending}
      failure={failure}
      confirming={confirming}
      reason={reason}
      onAsk={() => {
        setFailure(null)
        setConfirming(true)
      }}
      onCancel={() => setConfirming(false)}
      onReason={setReason}
      onPause={() => change.mutate({ threadId, mode: 'human_paused', reason })}
      onResume={() => change.mutate({ threadId, mode: 'ai_active', reason: '' })}
    />
  )
}

/**
 * What the control shows, given only what is known about the conversation.
 *
 * Kept apart from the mutation so that it can have nothing else to show. What
 * appears is a function of the mode the server stores and of how the last
 * attempt went — never of what somebody just pressed — so a refusal leaves the
 * conversation displaying the state it is actually in. Exported for the checks,
 * which are the place that guarantee it.
 */
export function ThreadModeView({
  mode,
  mayPause,
  busy,
  failure,
  confirming,
  reason,
  onAsk,
  onCancel,
  onReason,
  onPause,
  onResume,
}: {
  readonly mode: string
  readonly mayPause: boolean
  readonly busy: boolean
  readonly failure: string | null
  readonly confirming: boolean
  readonly reason: string
  readonly onAsk: () => void
  readonly onCancel: () => void
  readonly onReason: (reason: string) => void
  readonly onPause: () => void
  readonly onResume: () => void
}) {
  const paused = mode === 'human_paused'
  const active = mode === 'ai_active'

  // handoff_pending, blocked and closed are not this control's business. The
  // endpoint would take them anywhere, but "let the assistant go again" out of
  // blocked is a decision about the contact wearing a button about the
  // assistant, and handoff_pending has already stopped on its own.
  if (!paused && !active) {
    return failure === null ? null : <Alert tone="error">{failure}</Alert>
  }

  return (
    <>
      {paused ? (
        <Alert tone="warning">
          <div>
            <strong>{t('pause.title')}</strong>
            <div>{t('pause.body')}</div>
            <div>{t('pause.onlyThisButton')}</div>
            {mayPause ? (
              <div className="draft__actions draft__actions--inline">
                {/* One press. Nothing goes out because of it; drafts merely
                    start appearing again, and a manager reads them. */}
                <Button variant="secondary" busy={busy} onClick={onResume}>
                  {busy ? t('pause.resuming') : t('pause.resume')}
                </Button>
              </div>
            ) : null}
          </div>
        </Alert>
      ) : null}

      {failure === null ? null : <Alert tone="error">{failure}</Alert>}

      {!active || !mayPause ? null : confirming ? (
        <div className="confirm">
          <p className="confirm__title">{t('pause.confirmTitle')}</p>
          <p className="confirm__hint">{t('pause.confirmHint')}</p>
          {/* The sentence that stops the wrong pause. Somebody who has just
              answered a client by hand is the likeliest person to be standing
              here, and they do not need this button (docs/adr/0003). */}
          <p className="confirm__hint">{t('pause.notManualReply')}</p>
          <TextArea
            label={t('pause.reasonLabel')}
            value={reason}
            rows={2}
            disabled={busy}
            onChange={(event) => onReason(event.target.value)}
          />
          <div className="draft__actions">
            <Button variant="secondary" busy={busy} onClick={onPause}>
              {busy ? t('pause.pausing') : t('pause.confirm')}
            </Button>
            <Button variant="ghost" disabled={busy} onClick={onCancel}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="draft__actions">
          <Button variant="ghost" onClick={onAsk}>
            {t('pause.start')}
          </Button>
        </div>
      )}
    </>
  )
}
